import { ataChaptersQuery } from "@common/api/ata-chapter"
import {
  programmeAircraftQuery,
  programmeChecksQuery,
  programmeQuery,
  programmeRevisionsQuery,
  programmeTasksQuery,
} from "@common/api/maintenance-programme"
import type {
  ProgrammeDetailPublic,
  ProgrammeRevisionDetailPublic,
} from "@common/client"
import { LoadingState } from "@common/components/shared/loading-state"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useMatch,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { History } from "lucide-react"
import { Suspense } from "react"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListPage, type Pane } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requireProgrammePages } from "@/routes/_layout/maintenance-programmes/-lib/gate"
import {
  enrolmentBlocker,
  isArchived,
} from "@/routes/_layout/maintenance-programmes/-lib/programmes"
import { ActivityFeed } from "./-components/activity-feed"
import {
  AircraftActions,
  AircraftFilters,
} from "./-components/aircraft-controls"
import { AircraftTable } from "./-components/aircraft-table"
import type { PhaseRowActions, TaskRowActions } from "./-components/item-rows"
import {
  ItemFilters,
  ItemsActions,
  PhasesActions,
} from "./-components/items-controls"
import { ItemsTable, PhasesTable } from "./-components/items-table"
import { OverviewTab } from "./-components/overview-tab"
import {
  RevisionFilters,
  RevisionsActions,
} from "./-components/revisions-controls"
import { RevisionsTable } from "./-components/revisions-table"
import {
  AIRCRAFT_SIZING,
  aircraftDisplayOf,
  NO_AIRCRAFT_FILTERS,
} from "./-lib/aircraft"
import { COLUMN_SIZING } from "./-lib/columns"
import { NO_ITEM_FILTERS } from "./-lib/filters"
import { editBlocker, useViewedRevision, viewedRevision } from "./-lib/revision"
import {
  NO_REVISION_FILTERS,
  REVISION_SIZING,
  revisionsDisplayOf,
} from "./-lib/revisions"
import {
  CHECKS,
  displayOf,
  ENROLMENTS,
  programmeSearchSchema,
  REVISIONS,
  sortSearch,
  TASKS,
} from "./-lib/search"
import { nextSort } from "./-lib/table"
import { TABS, type Tab, tabLabel } from "./-lib/tabs"
import { useUpdateSearch } from "./-lib/use-update-search"

const TASK_ROUTE = "/_layout/maintenance-programmes_/$programmeId/tasks/$taskId"

const PHASE_ROUTE =
  "/_layout/maintenance-programmes_/$programmeId/phases/$phaseId"

const PANE_ROUTES: string[] = [
  TASK_ROUTE,
  "/_layout/maintenance-programmes_/$programmeId/tasks/new",
  PHASE_ROUTE,
  "/_layout/maintenance-programmes_/$programmeId/phases/new",
  "/_layout/maintenance-programmes_/$programmeId/revisions/new",
  "/_layout/maintenance-programmes_/$programmeId/aircraft/enrol",
]

export const Route = createFileRoute(
  "/_layout/maintenance-programmes_/$programmeId",
)({
  validateSearch: (search) => programmeSearchSchema.parse(search),
  loaderDeps: ({ search: { revision } }) => ({ revision }),
  beforeLoad: ({ context: { queryClient } }) =>
    requireProgrammePages(queryClient),
  loader: async ({
    context: { queryClient },
    params: { programmeId },
    deps: { revision },
  }) => {
    void queryClient.prefetchQuery(ataChaptersQuery())
    void queryClient.prefetchQuery(
      programmeAircraftQuery({ programme_id: programmeId, ...ENROLMENTS }),
    )
    const [programme, revisions] = await Promise.all([
      queryClient.ensureQueryData(
        programmeQuery({ programme_id: programmeId }),
      ),
      queryClient.ensureQueryData(
        programmeRevisionsQuery({ programme_id: programmeId, ...REVISIONS }),
      ),
    ])
    const viewed = viewedRevision(programme, revisions.data, revision)
    if (!viewed) return
    const scope = { programme_id: programmeId, revision_id: viewed.id }
    void queryClient.prefetchQuery(programmeTasksQuery({ ...scope, ...TASKS }))
    void queryClient.prefetchQuery(
      programmeChecksQuery({ ...scope, ...CHECKS }),
    )
  },
  component: ProgrammePage,
})

function paneOf(open: boolean, wide: boolean | undefined): Pane {
  if (!open) return "closed"
  return wide ? "full" : "split"
}

function ProgrammePage(): React.ReactElement {
  const { programmeId } = Route.useParams()
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const { data: programme } = useSuspenseQuery(
    programmeQuery({ programme_id: programmeId }),
  )
  const revision = useViewedRevision(programme)
  const [ref, width] = useWidth<HTMLDivElement>()
  const tab = search.tab ?? "tasks"
  const leaf = useRouterState({
    select: (state) => state.matches.at(-1)?.routeId,
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const open = leaf !== undefined && PANE_ROUTES.includes(leaf) && !compact

  return (
    <ListPage
      toolbar={
        <ListToolbar
          tabs={
            <ListTabs
              tabs={TABS}
              tab={tab}
              label={tabLabel}
              onSelect={(next) =>
                update({ tab: next === "tasks" ? undefined : next })
              }
            />
          }
          actions={
            revision && (
              <TabActions
                tab={tab}
                programme={programme}
                revision={revision}
                width={width}
              />
            )
          }
        />
      }
      pane={paneOf(open, search.wide)}
      detail={
        <Suspense fallback={<LoadingState />}>
          <Outlet />
        </Suspense>
      }
      scrollRef={ref}
    >
      {revision ? (
        <Suspense fallback={<LoadingState />}>
          <TabContent
            tab={tab}
            programme={programme}
            revision={revision}
            width={width}
          />
        </Suspense>
      ) : (
        <ListEmpty
          icon={History}
          title="No revisions"
          body="Tasks and aircraft appear here once the program has a revision."
        />
      )}
    </ListPage>
  )
}

function TabActions({
  tab,
  programme,
  revision,
  width,
}: {
  tab: Tab
  programme: ProgrammeDetailPublic
  revision: ProgrammeRevisionDetailPublic
  width: number | undefined
}): React.ReactNode {
  switch (tab) {
    case "tasks":
      return (
        <ItemsActions programme={programme} revision={revision} width={width} />
      )
    case "phases":
      return <PhasesActions programme={programme} revision={revision} />
    case "aircraft":
      return <AircraftActions programme={programme} width={width} />
    case "revisions":
      return <RevisionsActions programme={programme} width={width} />
    case "overview":
    case "activity":
      return null
  }
}

function TabContent({
  tab,
  programme,
  revision,
  width,
}: {
  tab: Tab
  programme: ProgrammeDetailPublic
  revision: ProgrammeRevisionDetailPublic
  width: number | undefined
}): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const navigate = useNavigate()
  const canUpdate = useHasPermission("maintenance_programme:update")
  const opened = useMatch({ from: TASK_ROUTE, shouldThrow: false })
  const openedPhase = useMatch({ from: PHASE_ROUTE, shouldThrow: false })
  const scope = { programme_id: programme.id, revision_id: revision.id }
  const params = { programmeId: programme.id }
  const itemSizing = useColumnSizing(displayOf(search).columns, COLUMN_SIZING)
  const aircraftSizing = useColumnSizing(
    aircraftDisplayOf(search).columns,
    AIRCRAFT_SIZING,
  )
  const revisionSizing = useColumnSizing(
    revisionsDisplayOf(search).columns,
    REVISION_SIZING,
  )
  const taskActions: TaskRowActions = {
    selected: opened?.params.taskId,
    open: (task) =>
      navigate({
        to:
          task.id === opened?.params.taskId
            ? "/maintenance-programmes/$programmeId"
            : "/maintenance-programmes/$programmeId/tasks/$taskId",
        params: { ...params, taskId: task.id },
        search: true,
      }),
    remove: editBlocker(programme, revision, canUpdate)
      ? undefined
      : (task) =>
          navigate({
            to: "/maintenance-programmes/$programmeId/tasks/remove/$taskId",
            params: { ...params, taskId: task.id },
            search: true,
          }),
  }
  const phaseActions: PhaseRowActions = {
    selected: openedPhase?.params.phaseId,
    open: (phase) =>
      navigate({
        to: "/maintenance-programmes/$programmeId/phases/$phaseId",
        params: { ...params, phaseId: phase.id },
        search: true,
      }),
    remove: editBlocker(programme, revision, canUpdate)
      ? undefined
      : (phase) =>
          navigate({
            to: "/maintenance-programmes/$programmeId/phases/remove/$phaseId",
            params: { ...params, phaseId: phase.id },
            search: true,
          }),
  }

  switch (tab) {
    case "overview":
      return <OverviewTab programme={programme} revision={revision} />
    case "tasks": {
      const display = displayOf(search)
      const { shown } = fitColumns(display.columns, width, itemSizing)
      return (
        <>
          <ItemFilters scope={scope} />
          <ItemsTable
            scope={scope}
            display={display}
            columns={shown}
            filters={{ ata: search.ata, phase: search.phase }}
            search={search.search}
            actions={taskActions}
            onSort={(column) => update(sortSearch(display, column))}
            onClear={() => update({ ...NO_ITEM_FILTERS, search: undefined })}
          />
        </>
      )
    }
    case "phases": {
      const display = displayOf(search)
      const { shown } = fitColumns(
        display.columns.filter((column) => column !== "phases"),
        width,
        itemSizing,
      )
      return (
        <>
          <ItemFilters scope={scope} />
          <PhasesTable
            scope={scope}
            display={display}
            columns={shown}
            filters={{ ata: search.ata, phase: search.phase }}
            search={search.search}
            actions={taskActions}
            phaseActions={phaseActions}
            onSort={(column) => update(sortSearch(display, column))}
            onClear={() => update({ ...NO_ITEM_FILTERS, search: undefined })}
          />
        </>
      )
    }
    case "aircraft": {
      const display = aircraftDisplayOf(search)
      const { shown } = fitColumns(display.columns, width, aircraftSizing)
      return (
        <>
          <AircraftFilters programmeId={programme.id} />
          <AircraftTable
            programmeId={programme.id}
            display={display}
            columns={shown}
            filters={{ aircraft_model: search.aircraft_model }}
            onRemove={
              enrolmentBlocker(programme, canUpdate)
                ? undefined
                : (enrolment) =>
                    navigate({
                      to: "/maintenance-programmes/$programmeId/aircraft/remove/$enrolmentId",
                      params: { ...params, enrolmentId: enrolment.id },
                      search: true,
                    })
            }
            onSort={(column) => {
              const next = nextSort(display, column, "registration")
              update({
                aircraft_sort: next.sort,
                aircraft_direction: next.direction,
              })
            }}
            onClear={() => update(NO_AIRCRAFT_FILTERS)}
          />
        </>
      )
    }
    case "revisions": {
      const display = revisionsDisplayOf(search)
      const { shown } = fitColumns(display.columns, width, revisionSizing)
      return (
        <>
          <RevisionFilters />
          <RevisionsTable
            programme={programme}
            viewed={revision}
            display={display}
            columns={shown}
            filters={{ revision_status: search.revision_status }}
            onView={(picked) =>
              update({
                tab: undefined,
                revision:
                  picked.id === programme.current_revision?.id
                    ? undefined
                    : picked.id,
              })
            }
            onRelease={
              canUpdate && !isArchived(programme)
                ? (picked) =>
                    navigate({
                      to: "/maintenance-programmes/$programmeId/revisions/release/$revisionId",
                      params: { ...params, revisionId: picked.id },
                      search: true,
                    })
                : undefined
            }
            onSort={(column) => {
              const next = nextSort(display, column, "date")
              update({
                revision_sort: next.sort,
                revision_direction: next.direction,
              })
            }}
            onClear={() => update(NO_REVISION_FILTERS)}
          />
        </>
      )
    }
    case "activity":
      return <ActivityFeed programmeId={programme.id} />
  }
}
